import {inject, Injectable} from '@angular/core';
import {
  GetFormatValueParams,
  GetNegativePercentParams,
  GetQuantityFromDataParams,
  GetQuantityParams, GetTotalValueParams
} from "../types/daily-feedback-reports-row.types";
import {
  DailyFeedbackReportsMarketplaceData
} from "../types/daily-feedback-reports-page.types";
import {DailyFeedbackReportsAggregationDataService} from "./daily-feedback-reports-aggregation-data.service";
import {UtilityService} from "../../../../core/services/utility.service";

@Injectable({
  providedIn: 'root'
})
export class DailyFeedbackReportsRowService {
  private dailyFeedbackReportsAggregationDataService: DailyFeedbackReportsAggregationDataService = inject(DailyFeedbackReportsAggregationDataService);
  private utilityService: UtilityService = inject(UtilityService);

  public getAggregationQuantity({ quantityType, marketplace }: GetQuantityParams): string {
    const aggregationData = this.dailyFeedbackReportsAggregationDataService.dailyFeedbackReportsAggregationData();
    return this.getQuantityFromData({
      quantityType: quantityType,
      marketplace: marketplace,
      data: aggregationData
    });
  }

  public getReportQuantity({ quantityType, marketplace, data }: GetQuantityParams): string {
    return this.getQuantityFromData({
      quantityType: quantityType,
      marketplace: marketplace,
      data: data
    });
  }

  public getAggregationTotalValue({ valueType }: GetTotalValueParams): string {
    const aggregationData = this.dailyFeedbackReportsAggregationDataService.dailyFeedbackReportsAggregationData();
    return this.getTotalValueFromData({
      valueType: valueType,
      data: aggregationData
    });
  }

  public getReportTotalValue(getTotalValueParams: GetTotalValueParams): string {
    return this.getTotalValueFromData(getTotalValueParams);
  }

  public getNegativePercent({ marketplace, type, data }: GetNegativePercentParams): string {
    if (!data) { return '0'; }

    const propertyMap = {
      ozon: {
        1: 'ozonMark1NegativePercent',
        2: 'ozonMark2NegativePercent',
        3: 'ozonMark3NegativePercent',
        total: 'ozonMarkNegativePercent'
      },
      wildberries: {
        1: 'wildberriesMark1NegativePercent',
        2: 'wildberriesMark2NegativePercent',
        3: 'wildberriesMark3NegativePercent',
        total: 'wildberriesMarkNegativePercent'
      }
    };

    if (!(marketplace in propertyMap)) { return '0'; }

    const marketplaceKey = marketplace as keyof DailyFeedbackReportsMarketplaceData;
    const marketplaceData = data[marketplaceKey];

    if (!marketplaceData) { return '0'; }

    const propertyName = propertyMap[marketplaceKey][type];
    const value = marketplaceData[propertyName as keyof typeof marketplaceData];
    return this.getFormatValue({
      isPercent: true,
      value: value
    });
  }

  private getTotalValueFromData({ valueType, data }: GetTotalValueParams): string {
    if (!data) { return '0';}

    const isPercent = valueType.includes('Percent');
    const value = data[valueType] !== undefined && data[valueType] !== null ? data[valueType] : null;

    return this.getFormatValue({
      isPercent: isPercent,
      value: Number(value)
    });
  }

  private getFormatValue({ isPercent, value }: GetFormatValueParams) {
    if (value === null || value === undefined) { return '0'; }

    const formatValue = this.utilityService.formatNumberForDisplay({
      value: Number(value),
      ...(isPercent ? { fractional: { value: 2 } } : { numberSeparator: { separator: ' ' } })
    });

    return isPercent ? `${formatValue}%` : formatValue;
  }

  private getQuantityFromData({ quantityType, marketplace, data }: GetQuantityFromDataParams): string {
    if (!data) { return '0'; }

    const type = `${marketplace}${quantityType}MarkQuantity`;
    const marketplaceData = data[marketplace as keyof DailyFeedbackReportsMarketplaceData];

    if (marketplaceData && type in marketplaceData) {
      return this.getFormatValue({ value: marketplaceData[type as keyof typeof marketplaceData] });
    }

    return '0';
  }
}
